import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

/**
 * Authentication Context
 * Provides global authentication state and methods for the entire application
 */
const AuthContext = createContext();

/**
 * AuthProvider Component
 * Wraps application and provides authentication state
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Initialize authentication from localStorage on component mount
   */
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = authService.getToken();
        const userData = authService.getUser();
        const userRoles = authService.getRoles();
        const userSections = authService.getSections();

        if (token && userData) {
          setIsAuthenticated(true);
          setUser(userData);
          setRoles(userRoles || []);
          setSections(userSections || []);
        } else {
          setIsAuthenticated(false);
          setUser(null);
          setRoles([]);
          setSections([]);
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * Handle user login
   * @param {string} username - Username or email
   * @param {string} password - Password
   */
  const login = async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(username, password);

      setIsAuthenticated(true);
      setUser(response.user);
      setRoles(response.roles || []);
      setSections(response.accessibleSections || []);

      return response;
    } catch (err) {
      setError(err.message);
      setIsAuthenticated(false);
      setUser(null);
      setRoles([]);
      setSections([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle user logout
   */
  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setRoles([]);
    setSections([]);
    setError(null);
  };

  /**
   * Handle user registration
   * @param {string} username - Username
   * @param {string} email - Email
   * @param {string} password - Password
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   */
  const register = async (username, email, password, firstName, lastName) => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.register(username, email, password, firstName, lastName);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean}
   */
  const hasRole = (roleName) => {
    return roles.includes(roleName);
  };

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  const isAdmin = () => {
    return roles.includes('ADMIN');
  };

  /**
   * Check if user can access specific section
   * @param {string} sectionName - Section name
   * @returns {boolean}
   */
  const canAccessSection = (sectionName) => {
    if (isAdmin()) {
      return true; // Admins have access to all sections
    }
    return sections.some(s => s.name === sectionName);
  };

  /**
   * Get accessible section names
   * @returns {Array<string>}
   */
  const getAccessibleSectionNames = () => {
    if (isAdmin()) {
      return sections.map(s => s.name);
    }
    return sections.map(s => s.name);
  };

  const value = {
    isAuthenticated,
    user,
    roles,
    sections,
    loading,
    error,
    login,
    logout,
    register,
    hasRole,
    isAdmin,
    canAccessSection,
    getAccessibleSectionNames,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use AuthContext
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
